import {createSelector, EntityAdapter} from '@reduxjs/toolkit';
import {isArray, isPlainObject, without} from 'lodash';
import {NormalizedPayload, ReducerState} from '~/utils/thunk-api/type';
import {DEFAULT_SOURCE_REDUCER_STATE} from '~/utils/thunk-api/constants';
import {RootState} from '~/redux/root-reducer';
import {ensureSafeChaining, getObjNthItem} from '~/utils/helpers';

const fetchSuccess = (
  state: ReducerState,
  payload: NormalizedPayload<any>,
  options?: {
    entityName: string;
    sortComparer: (a: any, b: any) => number;
  },
) => {
  const {source, normalized, paginator, isLoadMore, ...rest} = payload;
  if (!source) {
    return;
  }

  let allIds = normalized.result;
  if (options?.sortComparer && normalized.entities[options.entityName]) {
    allIds = Object.values(normalized.entities[options.entityName])
      .sort(options.sortComparer)
      .map((item: any) => item.id);
  }

  ensureSafeChaining(state, ['sources', source], DEFAULT_SOURCE_REDUCER_STATE);
  state.sources[source] = {
    ...state.sources[source],
    paginator,
    allIds: isLoadMore ? [...new Set([...(state.sources[source].allIds || []), ...allIds])] : allIds,
    lastRequestAt: Date.now() / 1000,
    ...rest,
  };
};

const deleteSuccess = (state: ReducerState, id: any) => {
  Object.keys(state.sources).forEach(stateKey => {
    ensureSafeChaining(state, ['sources', stateKey, 'allIds'], []);
    state.sources[stateKey].allIds = without(state.sources[stateKey].allIds, id);
  });
};

const addIdsToSources = (state: ReducerState, ids: any[] | number | string, sources: string[]) => {
  const add = (source: string, id: number | string) => {
    if (state.sources[source].allIds?.indexOf(id) === -1) {
      state.sources[source].allIds!.unshift(id);
    }
  };

  sources.forEach(source => {
    if (ids) {
      ensureSafeChaining(state, ['sources', source, 'allIds'], []);
      if (isArray(ids)) {
        ids.forEach(id => add(source, id));
      } else {
        add(source, ids);
      }
    }
  });
};

const removeIdsFromSources = (state: ReducerState, ids: any[] | number | string, sources: string[]) => {
  sources.forEach(source => {
    if (ids) {
      ensureSafeChaining(state, ['sources', source, 'allIds'], []);
      if (isArray(ids)) {
        state.sources[source].allIds = state.sources[source].allIds?.filter(i => ids.indexOf(i) === -1);
      } else {
        state.sources[source].allIds = state.sources[source].allIds?.filter(i => i !== ids);
      }
    }
  });
};

const requestOK = (result: any, action: any) => action.fulfilled.match(result);
const allRequestsOK = (...resultActionPair: [any, any][]) =>
  resultActionPair.every(([result, action]) => {
    const isOk = requestOK(result, action);
    if (!isOk) {
      // eslint-disable-next-line no-console
      console.error('Check carefully your result and action pair!!');
    }
    return isOk;
  });
const requestFailed = (result: any, action: any) => action.rejected.match(result);

const createSelectAllBySource = (subStateName: keyof RootState, selectEntities: any, sources: string[]) =>
  sources?.map(source =>
    createSelector(
      // @ts-ignore
      [selectEntities, (state: RootState) => state[subStateName].sources[source] || DEFAULT_SOURCE_REDUCER_STATE],
      (entities: any, {allIds = [], ...rest}) => ({
        data: allIds.map((id: number) => entities[id]),
        ...rest,
      }),
    ),
  );

// Upsert ignoring nested object!
const upsertMany = <T>(adapter: EntityAdapter<T>, state: any, entities: Record<string | number, any> = {}) => {
  const addedEntities = {} as Record<string | number, any>;
  Object.keys(entities).forEach(entityId => {
    const entity = entities[entityId];
    if (!state.entities[entityId]) {
      addedEntities[entityId] = entity;
    } else {
      const existedEntity = state.entities[entityId];
      let shouldUpdate = true;
      if (isPlainObject(entity)) {
        shouldUpdate = false;
        Object.keys(entity).forEach(prop => {
          const value = entity[prop];
          if (!isPlainObject(value) && value !== existedEntity[prop]) {
            shouldUpdate = true;
          }
        });
      }
      if (shouldUpdate) {
        adapter.updateOne(state, entity);
      }
    }
  });
  adapter.addMany(state, addedEntities);
};

// Upsert mutate means always replace existing object! Always trigger re-render
// Try to avoid using it
const upsertManyMutably = <T>(adapter: EntityAdapter<T>, state: any, entities: Record<string | number, any> = {}) => {
  adapter.upsertMany(state, entities);
};
const upsertOneMutably = <T>(adapter: EntityAdapter<T>, state: any, entities: Record<string | number, any> = {}) => {
  const entity = getObjNthItem(entities, 1);
  if (entity) {
    adapter.upsertOne(state, entity);
  }
};

export {
  fetchSuccess,
  deleteSuccess,
  addIdsToSources,
  removeIdsFromSources,
  requestOK,
  requestFailed,
  createSelectAllBySource,
  upsertMany,
  upsertOneMutably,
  upsertManyMutably,
  allRequestsOK,
};
